/**
 * Eigene Kochbücher: Liste und Editor. Im Editor stehen Titel, Vorwort,
 * Titelbild und Gestaltung, darunter die Kapitel mit ihren Rezepten in
 * fester Reihenfolge. Rezepte kommen einzeln über die Suche oder gesammelt
 * aus Kategorien, Schlagwörtern und der Merkliste hinzu. Erzeugt wird im
 * Server im Hintergrund; die Seite zeigt den Fortschritt und danach den
 * Download.
 */
import { formatDecimal } from '@cookbook/domain';
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ApiError, api } from '../api/client.ts';
import type {
  Cookbook,
  CookbookBuild,
  CookbookChapter,
  CookbookSettings,
  RecipeSummary,
} from '../api/types.ts';
import { useQuery } from '../api/useQuery.ts';
import { AppIcon } from '../components/AppIcon.tsx';
import { Panel } from '../components/Panel.tsx';
import { useCatalog } from '../state/catalog.ts';
import { texts } from '../texts/de.ts';

/** Antwort der Schnittstelle zu einem Kochbuch. */
interface CookbookResponse {
  cookbook: Cookbook;
  build: CookbookBuild;
}

/** Bearbeitbarer Teil eines Kochbuchs. */
interface Draft {
  title: string;
  subtitle: string;
  preface: string;
  settings: CookbookSettings;
  chapters: CookbookChapter[];
}

/** Eingabefeld im einheitlichen Stil. */
const inputClass = 'w-full rounded-lg border border-border-subtle bg-surface px-2 py-1.5';

/** Dateigröße lesbar. */
function sizeText(bytes: number): string {
  return bytes >= 1024 * 1024
    ? `${formatDecimal(bytes / 1024 / 1024, 1)} MB`
    : `${formatDecimal(Math.max(1, Math.round(bytes / 1024)), 0)} KB`;
}

/** Verschiebt einen Eintrag in einer Liste. */
function move<T>(list: readonly T[], index: number, by: -1 | 1): T[] {
  const target = index + by;
  if (target < 0 || target >= list.length) {
    return [...list];
  }
  const next = [...list];
  [next[index], next[target]] = [next[target] as T, next[index] as T];
  return next;
}

/** Kleiner Knopf mit Piktogramm-Text (↑ ↓ ✕). */
function IconButton({
  label,
  onClick,
  children,
  disabled = false,
}: {
  readonly label: string;
  readonly onClick: () => void;
  readonly children: React.ReactNode;
  readonly disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="rounded-lg px-2 py-1 text-content-muted hover:bg-surface-sunken disabled:opacity-30"
    >
      {children}
    </button>
  );
}

/** Kapitel mit Rezepten, umbenennen und sortieren. */
function ChaptersEditor({
  chapters,
  titles,
  target,
  onTarget,
  onChange,
}: {
  readonly chapters: readonly CookbookChapter[];
  readonly titles: ReadonlyMap<string, string>;
  readonly target: number;
  readonly onTarget: (index: number) => void;
  readonly onChange: (chapters: CookbookChapter[]) => void;
}) {
  const update = (index: number, chapter: CookbookChapter) =>
    onChange(chapters.map((entry, position) => (position === index ? chapter : entry)));
  return (
    <div className="space-y-3">
      {chapters.map((chapter, index) => (
        <section
          key={index}
          className={`rounded-xl border p-3 ${index === target ? 'border-accent' : 'border-border-subtle'}`}
          onClick={() => onTarget(index)}
        >
          <div className="mb-2 flex items-center gap-1">
            <input
              value={chapter.title}
              onChange={(event) => update(index, { ...chapter, title: event.target.value })}
              placeholder={texts.cookbooks.chapterTitle}
              aria-label={texts.cookbooks.chapterTitle}
              className={`${inputClass} font-semibold`}
            />
            <IconButton
              label={texts.cookbooks.up}
              onClick={() => onChange(move(chapters, index, -1))}
              disabled={index === 0}
            >
              ↑
            </IconButton>
            <IconButton
              label={texts.cookbooks.down}
              onClick={() => onChange(move(chapters, index, 1))}
              disabled={index === chapters.length - 1}
            >
              ↓
            </IconButton>
            <IconButton
              label={texts.cookbooks.removeChapter}
              onClick={() => onChange(chapters.filter((_, position) => position !== index))}
            >
              ✕
            </IconButton>
          </div>
          {chapter.recipeIds.length === 0 ? (
            <p className="text-sm text-content-muted">{texts.cookbooks.emptyChapter}</p>
          ) : (
            <ol className="space-y-1 text-sm">
              {chapter.recipeIds.map((recipeId, position) => (
                <li key={`${recipeId}-${position}`} className="flex items-center gap-1">
                  <span className="w-6 text-right text-xs text-content-muted tabular-nums">
                    {position + 1}
                  </span>
                  <span className="min-w-0 flex-1 truncate">{titles.get(recipeId) ?? '…'}</span>
                  <IconButton
                    label={texts.cookbooks.up}
                    onClick={() =>
                      update(index, { ...chapter, recipeIds: move(chapter.recipeIds, position, -1) })
                    }
                    disabled={position === 0}
                  >
                    ↑
                  </IconButton>
                  <IconButton
                    label={texts.cookbooks.down}
                    onClick={() =>
                      update(index, { ...chapter, recipeIds: move(chapter.recipeIds, position, 1) })
                    }
                    disabled={position === chapter.recipeIds.length - 1}
                  >
                    ↓
                  </IconButton>
                  <IconButton
                    label={texts.cookbooks.remove}
                    onClick={() =>
                      update(index, {
                        ...chapter,
                        recipeIds: chapter.recipeIds.filter((_, entry) => entry !== position),
                      })
                    }
                  >
                    ✕
                  </IconButton>
                </li>
              ))}
            </ol>
          )}
        </section>
      ))}
      <button
        type="button"
        onClick={() => {
          onChange([...chapters, { title: '', recipeIds: [] }]);
          onTarget(chapters.length);
        }}
        className="rounded-xl border border-border-subtle px-3 py-2 text-sm"
      >
        + {texts.cookbooks.addChapter}
      </button>
    </div>
  );
}

/** Rezepte hinzufügen: gesammelt als Kapitel oder einzeln über die Suche. */
function RecipeAdder({
  draft,
  target,
  onChange,
}: {
  readonly draft: Draft;
  readonly target: number;
  readonly onChange: (chapters: CookbookChapter[]) => void;
}) {
  const catalog = useCatalog();
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('');
  const inBook = useMemo(
    () => new Set(draft.chapters.flatMap((chapter) => chapter.recipeIds)),
    [draft.chapters],
  );
  const byTitle = (a: RecipeSummary, b: RecipeSummary) => a.title.localeCompare(b.title, 'de');

  /** Fügt ein Rezept dem gewählten Kapitel hinzu (oder einem neuen, wenn es keins gibt). */
  function addRecipe(recipeId: string) {
    if (draft.chapters.length === 0) {
      onChange([{ title: '', recipeIds: [recipeId] }]);
      return;
    }
    const index = Math.min(target, draft.chapters.length - 1);
    onChange(
      draft.chapters.map((chapter, position) =>
        position === index ? { ...chapter, recipeIds: [...chapter.recipeIds, recipeId] } : chapter,
      ),
    );
  }

  /** Ein Kapitel je Kategorie mit Rezepten. */
  function fromCategories() {
    const added = [...catalog.categories]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((category) => ({
        title: category.name,
        recipeIds: catalog.recipes
          .filter((recipe) => recipe.categoryIds.includes(category.id))
          .sort(byTitle)
          .map((recipe) => recipe.id),
      }))
      .filter((chapter) => chapter.recipeIds.length > 0);
    onChange([...draft.chapters.filter((chapter) => chapter.recipeIds.length > 0), ...added]);
  }

  /** Ein Kapitel aus einer Kategorie, einem Schlagwort oder der Merkliste. */
  function fromSource() {
    const [kind, value = ''] = source.split(':');
    const recipes = catalog.recipes
      .filter((recipe) =>
        kind === 'kategorie'
          ? recipe.categoryIds.includes(value)
          : kind === 'schlagwort'
            ? recipe.tagNames.includes(value)
            : recipe.isFavorite,
      )
      .sort(byTitle);
    const title =
      kind === 'kategorie'
        ? (catalog.categories.find((category) => category.id === value)?.name ?? '')
        : kind === 'schlagwort'
          ? value
          : texts.cookbooks.favoritesChapter;
    onChange([...draft.chapters, { title, recipeIds: recipes.map((recipe) => recipe.id) }]);
  }

  const needle = query.trim().toLowerCase();
  const results =
    needle.length < 2
      ? []
      : catalog.recipes
          .filter((recipe) => recipe.title.toLowerCase().includes(needle))
          .sort(byTitle)
          .slice(0, 12);

  return (
    <div className="space-y-3 text-sm">
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={fromCategories}
          disabled={catalog.recipes.length === 0}
          className="rounded-xl border border-border-subtle px-3 py-2 disabled:opacity-50"
        >
          {texts.cookbooks.fromCategories}
        </button>
      </div>
      <div className="flex gap-2">
        <select
          value={source}
          onChange={(event) => setSource(event.target.value)}
          aria-label={texts.cookbooks.fromSource}
          className={inputClass}
        >
          <option value="">…</option>
          <option value="merkliste">{texts.cookbooks.favorites}</option>
          {catalog.categories
            .filter((category) => category.recipeCount > 0)
            .map((category) => (
              <option key={category.id} value={`kategorie:${category.id}`}>
                {texts.cookbooks.category.replace('{name}', category.name)}
              </option>
            ))}
          {catalog.tags
            .filter((tag) => tag.recipeCount > 0)
            .map((tag) => (
              <option key={tag.id} value={`schlagwort:${tag.name}`}>
                {texts.cookbooks.tag.replace('{name}', tag.name)}
              </option>
            ))}
        </select>
        <button
          type="button"
          onClick={fromSource}
          disabled={source === ''}
          className="shrink-0 rounded-xl border border-border-subtle px-3 py-2 disabled:opacity-50"
        >
          {texts.cookbooks.fromSource}
        </button>
      </div>
      <div>
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={texts.cookbooks.search}
          aria-label={texts.cookbooks.search}
          className={inputClass}
        />
        {draft.chapters.length === 0 ? null : (
          <p className="mt-1 text-xs text-content-muted">
            {texts.cookbooks.addTo}:{' '}
            <strong>
              {draft.chapters[Math.min(target, draft.chapters.length - 1)]?.title ||
                texts.cookbooks.newChapter}
            </strong>
          </p>
        )}
        <ul className="mt-2 divide-y divide-border-subtle">
          {results.map((recipe) => (
            <li key={recipe.id}>
              <button
                type="button"
                onClick={() => addRecipe(recipe.id)}
                className="flex w-full items-center justify-between gap-2 py-1.5 text-left"
              >
                <span className="truncate">{recipe.title}</span>
                <span className="shrink-0 text-xs text-content-muted">
                  {inBook.has(recipe.id) ? texts.cookbooks.added : '+'}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Erzeugen mit Fortschritt und Download. */
function BuildPanel({
  cookbook,
  build,
  dirty,
  onSave,
  onBuild,
}: {
  readonly cookbook: Cookbook;
  readonly build: CookbookBuild;
  readonly dirty: boolean;
  readonly onSave: () => Promise<boolean>;
  readonly onBuild: (build: CookbookBuild) => void;
}) {
  const [message, setMessage] = useState<string | null>(null);
  const running = build.status === 'running';

  async function start() {
    setMessage(null);
    if (dirty && !(await onSave())) {
      return;
    }
    try {
      const response = await api.post<{ build: CookbookBuild }>(`/api/cookbooks/${cookbook.id}/build`);
      onBuild(response.build);
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  return (
    <Panel title={texts.cookbooks.build}>
      <div className="space-y-3 text-sm">
        {running ? (
          <div>
            <p className="mb-1">
              {texts.cookbooks.building.replace('{percent}', String(Math.round(build.progress * 100)))}
            </p>
            <div className="h-2 overflow-hidden rounded-full bg-surface-sunken">
              <div
                className="h-full rounded-full bg-accent transition-[width]"
                style={{ width: `${Math.round(build.progress * 100)}%` }}
              />
            </div>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => void start()}
            disabled={cookbook.recipeCount === 0 && !dirty}
            className="rounded-xl bg-accent px-4 py-2.5 font-semibold text-on-accent disabled:opacity-50"
          >
            {build.builtAt === null ? texts.cookbooks.build : texts.cookbooks.rebuild}
          </button>
        )}
        <p className="text-xs text-content-muted">{texts.cookbooks.buildHint}</p>
        {build.status === 'failed' && build.message !== null ? (
          <p className="text-accent-strong">{texts.cookbooks.failed.replace('{message}', build.message)}</p>
        ) : null}
        {message === null ? null : <p className="text-accent-strong">{message}</p>}
        {build.builtAt === null || build.pages === null || build.bytes === null ? null : (
          <div className="flex flex-wrap items-center gap-3 rounded-xl bg-surface-sunken p-3">
            <a
              href={`/api/cookbooks/${cookbook.id}/pdf`}
              download
              className="inline-flex items-center gap-2 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2 font-medium"
            >
              <AppIcon name="library" size={18} />
              {texts.cookbooks.download}
            </a>
            <span className="text-xs text-content-muted">
              {texts.cookbooks.builtInfo
                .replace('{pages}', String(build.pages))
                .replace('{size}', sizeText(build.bytes))
                .replace('{date}', new Date(build.builtAt).toLocaleString('de-DE'))}
            </span>
            {build.stale || dirty ? (
              <p className="w-full text-xs text-accent-strong">{texts.cookbooks.stale}</p>
            ) : null}
          </div>
        )}
      </div>
    </Panel>
  );
}

/** Editor eines Kochbuchs. */
function CookbookEditor({ id }: { readonly id: string }) {
  const navigate = useNavigate();
  const { data, loading, error, setData } = useQuery<CookbookResponse>(`/api/cookbooks/${id}`);
  const catalog = useCatalog();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [dirty, setDirty] = useState(false);
  const [target, setTarget] = useState(0);
  const [message, setMessage] = useState<string | null>(null);
  const cookbook = data?.cookbook ?? null;
  const build = data?.build ?? null;

  useEffect(() => {
    if (cookbook !== null && draft === null) {
      setDraft({
        title: cookbook.title,
        subtitle: cookbook.subtitle ?? '',
        preface: cookbook.preface ?? '',
        settings: cookbook.settings,
        chapters: cookbook.chapters,
      });
    }
  }, [cookbook, draft]);

  // Während der Erzeugung jede Sekunde nach dem Stand fragen.
  useEffect(() => {
    if (build?.status !== 'running') {
      return undefined;
    }
    const timer = window.setInterval(() => {
      api
        .get<CookbookResponse>(`/api/cookbooks/${id}`)
        .then((response) => setData(response))
        .catch(() => undefined);
    }, 1000);
    return () => window.clearInterval(timer);
  }, [build?.status, id, setData]);

  const titles = useMemo(
    () => new Map(catalog.recipes.map((recipe) => [recipe.id, recipe.title])),
    [catalog.recipes],
  );

  if (loading && cookbook === null) {
    return <p className="text-sm text-content-muted">{texts.common.loading}</p>;
  }
  if (cookbook === null || build === null || draft === null) {
    return <p className="text-sm text-accent-strong">{error ?? texts.common.error}</p>;
  }

  const change = (next: Partial<Draft>) => {
    setDraft({ ...draft, ...next });
    setDirty(true);
  };
  const setting = (key: keyof CookbookSettings, value: boolean | 'A4' | 'A5') =>
    change({ settings: { ...draft.settings, [key]: value } });

  async function save(): Promise<boolean> {
    if (draft === null) {
      return false;
    }
    setMessage(null);
    try {
      const response = await api.put<CookbookResponse>(`/api/cookbooks/${id}`, draft);
      setData(response);
      setDraft({ ...draft, chapters: response.cookbook.chapters });
      setDirty(false);
      setMessage(texts.cookbooks.saved);
      return true;
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
      return false;
    }
  }

  async function uploadCover(file: File) {
    try {
      setData(await api.upload<CookbookResponse>(`/api/cookbooks/${id}/cover`, file));
    } catch (caught) {
      setMessage(caught instanceof ApiError ? caught.message : texts.common.error);
    }
  }

  return (
    <div className="space-y-4">
      <Link to="/kochbuecher" className="inline-flex items-center gap-1 text-sm text-content-muted">
        ← {texts.cookbooks.back}
      </Link>

      <Panel title={texts.cookbooks.head}>
        <div className="grid gap-3 text-sm sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">{texts.cookbooks.bookTitle}</span>
            <input
              value={draft.title}
              onChange={(event) => change({ title: event.target.value })}
              className={inputClass}
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs text-content-muted">{texts.cookbooks.bookSubtitle}</span>
            <input
              value={draft.subtitle}
              onChange={(event) => change({ subtitle: event.target.value })}
              className={inputClass}
            />
          </label>
          <label className="block sm:col-span-2">
            <span className="mb-1 block text-xs text-content-muted">
              {texts.cookbooks.preface} · {texts.cookbooks.prefaceHint}
            </span>
            <textarea
              value={draft.preface}
              onChange={(event) => change({ preface: event.target.value })}
              rows={4}
              className={inputClass}
            />
          </label>
          <div className="flex items-center gap-3 sm:col-span-2">
            <div className="flex h-16 w-24 items-center justify-center overflow-hidden rounded-xl bg-surface-sunken">
              {cookbook.coverThumbUrl === null ? (
                <AppIcon name="library" size={24} className="text-content-muted" />
              ) : (
                <img src={cookbook.coverThumbUrl} alt="" className="h-full w-full object-cover" />
              )}
            </div>
            <label className="cursor-pointer rounded-xl border border-border-subtle px-3 py-2">
              {texts.cookbooks.chooseCover}
              <input
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (file !== undefined) {
                    void uploadCover(file);
                  }
                }}
              />
            </label>
            {cookbook.coverUrl === null ? null : (
              <button
                type="button"
                onClick={() =>
                  void api
                    .delete<CookbookResponse>(`/api/cookbooks/${id}/cover`)
                    .then((response) => setData(response))
                }
                className="text-content-muted underline"
              >
                {texts.cookbooks.removeCover}
              </button>
            )}
          </div>
        </div>
      </Panel>

      <Panel title={texts.cookbooks.settings}>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-4" role="radiogroup" aria-label={texts.cookbooks.format}>
            <span className="text-content-muted">{texts.cookbooks.format}</span>
            {(['A4', 'A5'] as const).map((format) => (
              <label key={format} className="flex items-center gap-1.5">
                <input
                  type="radio"
                  name="kochbuch-format"
                  checked={draft.settings.format === format}
                  onChange={() => setting('format', format)}
                  className="accent-accent"
                />
                {format}
              </label>
            ))}
          </div>
          {(['photos', 'animations', 'nutrition', 'ratings', 'index'] as const).map((key) => (
            <label key={key} className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={draft.settings[key]}
                onChange={(event) => setting(key, event.target.checked)}
                className="accent-accent"
              />
              {texts.cookbooks[key]}
            </label>
          ))}
        </div>
      </Panel>

      <div className="grid gap-4 md:grid-cols-[1fr_18rem]">
        <Panel title={texts.cookbooks.chapters}>
          <ChaptersEditor
            chapters={draft.chapters}
            titles={titles}
            target={target}
            onTarget={setTarget}
            onChange={(chapters) => change({ chapters })}
          />
        </Panel>
        <Panel title={texts.cookbooks.add}>
          <RecipeAdder draft={draft} target={target} onChange={(chapters) => change({ chapters })} />
        </Panel>
      </div>

      <div className="sticky bottom-20 z-10 flex flex-wrap items-center gap-3 rounded-2xl border border-border-subtle bg-surface-raised p-3 shadow-sm md:bottom-4">
        <button
          type="button"
          onClick={() => void save()}
          disabled={!dirty}
          className="rounded-xl bg-accent px-4 py-2 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.cookbooks.save}
        </button>
        <span className="text-sm text-content-muted">
          {dirty
            ? texts.cookbooks.unsaved
            : (message ?? `${texts.cookbooks.recipes.replace('{count}', String(cookbook.recipeCount))}`)}
        </span>
        <button
          type="button"
          onClick={() => {
            if (window.confirm(texts.cookbooks.deleteConfirm)) {
              void api.delete(`/api/cookbooks/${id}`).then(() => navigate('/kochbuecher'));
            }
          }}
          className="ml-auto text-sm text-content-muted underline"
        >
          {texts.cookbooks.delete}
        </button>
      </div>

      <BuildPanel
        cookbook={cookbook}
        build={build}
        dirty={dirty}
        onSave={save}
        onBuild={(next) => setData({ cookbook, build: next })}
      />
    </div>
  );
}

/** Liste der Kochbücher mit Neuanlage. */
function CookbookList() {
  const navigate = useNavigate();
  const { data, loading, error } = useQuery<{ cookbooks: (Cookbook & { build: CookbookBuild })[] }>(
    '/api/cookbooks',
  );
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState<string | null>(null);

  return (
    <div className="space-y-4">
      <form
        className="flex gap-2"
        onSubmit={(event) => {
          event.preventDefault();
          setMessage(null);
          api
            .post<CookbookResponse>('/api/cookbooks', { title })
            .then((response) => navigate(`/kochbuecher/${response.cookbook.id}`))
            .catch((caught: unknown) =>
              setMessage(caught instanceof ApiError ? caught.message : texts.common.error),
            );
        }}
      >
        <input
          value={title}
          onChange={(event) => setTitle(event.target.value)}
          placeholder={texts.cookbooks.newTitle}
          aria-label={texts.cookbooks.newTitle}
          className="flex-1 rounded-xl border border-border-subtle bg-surface-raised px-3 py-2.5"
        />
        <button
          type="submit"
          disabled={title.trim() === ''}
          className="rounded-xl bg-accent px-4 font-semibold text-on-accent disabled:opacity-50"
        >
          {texts.cookbooks.create}
        </button>
      </form>
      {message === null ? null : <p className="text-sm text-accent-strong">{message}</p>}
      {loading && data === null ? <p className="text-sm text-content-muted">{texts.common.loading}</p> : null}
      {error === null ? null : <p className="text-sm text-accent-strong">{error}</p>}
      {data !== null && data.cookbooks.length === 0 ? (
        <p className="rounded-card border border-border-subtle bg-surface-raised p-6 text-center text-sm text-content-muted">
          {texts.cookbooks.empty}
        </p>
      ) : null}
      <ul className="grid gap-3 sm:grid-cols-2">
        {(data?.cookbooks ?? []).map((cookbook) => (
          <li key={cookbook.id}>
            <Link
              to={`/kochbuecher/${cookbook.id}`}
              className="flex gap-3 rounded-card border border-border-subtle bg-surface-raised p-3"
            >
              <div className="flex h-20 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-surface-sunken">
                {cookbook.coverThumbUrl === null ? (
                  <AppIcon name="library" size={24} className="text-content-muted" />
                ) : (
                  <img src={cookbook.coverThumbUrl} alt="" className="h-full w-full object-cover" />
                )}
              </div>
              <div className="min-w-0">
                <p className="truncate font-semibold">{cookbook.title}</p>
                <p className="truncate text-sm text-content-muted">{cookbook.subtitle}</p>
                <p className="mt-1 text-xs text-content-muted">
                  {texts.cookbooks.recipes.replace('{count}', String(cookbook.recipeCount))} ·{' '}
                  {cookbook.settings.format}
                </p>
              </div>
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Seite der Kochbücher. */
export function CookbooksPage() {
  const { id } = useParams();
  return (
    <section className="mx-auto max-w-4xl px-4 py-6">
      <header className="mb-4">
        <h1 className="text-2xl font-bold tracking-tight">{texts.cookbooks.title}</h1>
        <p className="mt-0.5 text-sm text-content-muted">{texts.cookbooks.subtitle}</p>
      </header>
      {id === undefined ? <CookbookList /> : <CookbookEditor key={id} id={id} />}
    </section>
  );
}
